import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { sidebarIcon } from "./sidebarIcon";
import { useDispatch, useSelector } from "react-redux";
import "./sidebar.css";
import type { AppDispatch } from "@/app/redux/store";
import { fetchMenuData } from "@/app/redux/reducers/apiSlice";
import { ICON_BASE_PATH, IMAGE_BASE_PATH } from "@/app/utils/config";

interface SidebarItem {
  title: string;
  icon: string;
  submenuOpen: boolean;
  path: string | null;
  submenu: { menuName: string; menuUrl?: string | null }[];
}

interface CfoMenuItem {
  label: string;
  icon: string;
  path?: string | null;
}

const Sidebar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeBottomIndex, setActiveBottomIndex] = useState<number | null>(
    null,
  );
  const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null);
  const [activeSubmenuIndex, setActiveSubmenuIndex] = useState<{
    menuIndex: number;
    subIndex: number;
  } | null>(null);
  const [isToggled, setIsToggled] = useState(false);
  const isCFO = location.pathname.startsWith("/CFO");
  const enabledMenus = useSelector((state: any) => state.api.data);
  const rawMenuData = useSelector((state: any) => state.api.rawData);
  const [menuTop, setMenuTop] = useState<SidebarItem[]>([]);
  const [menuBottom, setMenuBottom] = useState<SidebarItem[]>([]);
  const [menuConfig, setMenuConfig] = useState<CfoMenuItem[]>([]);
  const [activeCfoIndex, setActiveCfoIndex] = useState<number | null>(null);

  // ---------------- buildMenuItem ----------------

  const buildMenuItem = (icon: any, module?: any): SidebarItem => {
    const menus = module?.menus || icon?.menus || [];
    return {
      title: icon.menuName,
      icon: icon.menuIcon,
      submenuOpen: false,
      path: icon.moduleUrl || module?.moduleUrl || null,
      submenu: menus.map((sub: any) => ({
        menuName: sub.menuName,
        menuUrl: sub.menuUrl,
      })),
    };
  };

  // ---------------- fetchMenuData data list----------------

  useEffect(() => {
    dispatch(fetchMenuData());
  }, [dispatch]);

  // ---------------- sidebar menu list logical----------------
  useEffect(() => {
    if (!enabledMenus.length || isCFO) return;

    const topMenus = sidebarIcon
      .filter((i) => i.position !== "bottom")
      .map((icon) => {
        const module = enabledMenus.find(
          (m: any) => m.moduleId === icon.moduleId,
        );
        if (module) return buildMenuItem(icon, module);
        if ((icon as any)?.enableFlag === "Y") return buildMenuItem(icon);
        return null;
      })
      .filter(Boolean) as SidebarItem[];

    const bottomMenus = sidebarIcon
      .filter((i) => i.position === "bottom")
      .map((icon) => buildMenuItem(icon));

    setActiveMenuIndex(null);
    setActiveSubmenuIndex(null);
    setActiveBottomIndex(null);

    topMenus.forEach((menu, mIdx) => {
      if (menu.path === location.pathname) setActiveMenuIndex(mIdx);
      menu.submenu.forEach((sub, sIdx) => {
        if (sub.menuUrl === location.pathname) {
          menu.submenuOpen = true;
          setActiveMenuIndex(mIdx);
          setActiveSubmenuIndex({ menuIndex: mIdx, subIndex: sIdx });
        }
      });
    });

    bottomMenus.forEach((menu, idx) => {
      if (menu.path === location.pathname) setActiveBottomIndex(idx);
    });

    setMenuTop(topMenus);
    setMenuBottom(bottomMenus);
  }, [enabledMenus, location.pathname, isCFO]);

  // ----------------handleKeyDown ----------------

  const handleKeyDown = (e: React.KeyboardEvent, callback: () => void) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback();
    }
  };

  // ---------------- CFO siderbar list ----------------
  useEffect(() => {
    if (!rawMenuData?.length || !isCFO) return;

    const cfoModule = rawMenuData?.find((m: any) => m.moduleId === 73);

    if (cfoModule?.menus?.length) {
      const cfoMenus: CfoMenuItem[] = [
        {
          label: "Dashboard",
          icon: "dashboard.svg",
          path: "/CFO/Dashboard",
        },

        // {
        //   label: "Cashflow",
        //   icon: "cashflow.svg",
        //   path: "/CFO/Cashflow",
        // },
        { label: "Payables", icon: "payables.svg", path: "/CFO/Payables" },
        {
          label: "Receivables",
          icon: "receivables.svg",
          path: "/CFO/Receivables",
        },
        ...cfoModule?.menus
          .filter((menu: any) => menu.menuName === "Genie AI")
          .map((menu: any) => ({
            label: menu.menuName,
            icon: menu.menuIcon || "ix_ai.svg",
            path: "/CFO/genieAI",
          })),
        // { label: "Scenarios", icon: "scenarios.svg", path: "/CFO/Scenarios" },
        // { label: "Reports", icon: "cfo-reports.svg", path: "/CFO/Reports" },
        // { label: "Settings", icon: "cfo-settings.svg", path: "/CFO/Settings" },
      ];

      setMenuConfig(cfoMenus);
    }
  }, [rawMenuData, isCFO]);

  // ---------------- CFO leavet click open ----------------
  useEffect(() => {
    if (isCFO) setIsToggled(false);
  }, [isCFO]);

  // ---------------- toggleMenu ----------------

  const toggleMenu = (index: number) => {
    setMenuTop((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, submenuOpen: !item.submenuOpen } : item,
      ),
    );
  };

  // ---------------- menu list sidebar active funcation----------------

  useEffect(() => {
    if (!isCFO || menuConfig.length === 0) return;

    const activeIndex = menuConfig.findIndex((item) =>
      location.pathname
        .toLowerCase()
        .startsWith((item.path || "").toLowerCase()),
    );
    setActiveCfoIndex(activeIndex >= 0 ? activeIndex : null);
  }, [location.pathname, menuConfig, isCFO]);

  return (
    <div className="gx-sidebar-container">
      {!isCFO && (
        <div
          className={`sidebar-sub-container ${
            isToggled ? "collapsed" : "expanded"
          }`}
        >
          <div className="gx-sidebar" aria-label="Main Navigation">
            <div className="gx-logoWrapper flex items-center justify-between">
              <img
                className="growX-logo"
                src={`${IMAGE_BASE_PATH}logosider.png`}
                alt="Logo"
                width={140}
                height={40}
              />

              <div
                onClick={() => setIsToggled(!isToggled)}
                className="cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={isToggled ? "Expand sidebar" : "Collapse sidebar"}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setIsToggled(!isToggled);
                  }
                }}
              >
                <div className="gx-side-close-imge">
                  <img
                    width={40}
                    src={`${IMAGE_BASE_PATH}sidebarlogo.png`}
                    alt="Sidebar toggle icon"
                    aria-label="Sidebar toggle icon"
                  />
                </div>

                <div className="gx-toggle-icon">
                  <img
                    src={`${IMAGE_BASE_PATH}arrowsider.svg`}
                    alt={isToggled ? "Expand sidebar" : "Collapse sidebar"}
                    aria-label={
                      isToggled ? "Expand sidebar" : "Collapse sidebar"
                    }
                    width={18}
                    height={16}
                    style={{
                      transform: isToggled ? "rotate(90deg)" : "rotate(280deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ---------------- TOP MENU ---------------- */}
            <div className="gx-sidebar-menu">
              <div className="top-section">
                {menuTop.map((menu, index) => (
                  <div key={index} className="menu-section">
                    <div
                      className={`menu-item flex items-center ${
                        activeMenuIndex === index && !activeSubmenuIndex
                          ? "active"
                          : ""
                      }`}
                      role="button"
                      tabIndex={0}
                      aria-label={`${menu.title} menu`}
                      aria-current={
                        activeMenuIndex === index ? "page" : undefined
                      }
                      aria-expanded={
                        menu.submenu.length > 0 ? menu.submenuOpen : undefined
                      }
                      aria-haspopup={
                        menu.submenu.length > 0 ? "menu" : undefined
                      }
                      aria-controls={
                        menu.submenu.length > 0 ? `submenu-${index}` : undefined
                      }
                      onClick={() => {
                        if (menu.submenu.length > 0) {
                          toggleMenu(index);
                        } else if (menu.path) {
                          setActiveMenuIndex(index);
                          setActiveSubmenuIndex(null);
                          navigate(menu.path);
                        }
                      }}
                      onKeyDown={(e) =>
                        handleKeyDown(e, () => {
                          if (menu.submenu.length > 0) {
                            toggleMenu(index);
                          } else if (menu.path) {
                            setActiveMenuIndex(index);
                            setActiveSubmenuIndex(null);
                            navigate(menu.path);
                          }
                        })
                      }
                    >
                      <div className="icon-container">
                        <img
                          src={`${ICON_BASE_PATH}${menu.icon}`}
                          width={22}
                          className="menu-icon"
                          alt={menu.title}
                          aria-label={menu.title}
                        />
                      </div>

                      {!isToggled && (
                        <span className="gx-p14-b">{menu.title}</span>
                      )}
                      {menu.submenu.length > 0 && !isToggled && (
                        <img
                          aria-label="Select Arrow"
                          src={`${ICON_BASE_PATH}vector.svg`}
                          className={`submenu-arrow ${menu.submenuOpen ? "open" : ""}`}
                        />
                      )}
                    </div>

                    {menu.submenuOpen && !isToggled && (
                      <div className="submenu-container">
                        {menu.submenu.map((sub, i) => (
                          <div
                            key={i}
                            role="menuitem"
                            tabIndex={0}
                            aria-label={sub.menuName}
                            aria-expanded={menu.submenuOpen}
                            aria-haspopup="menu"
                            aria-controls={`submenu-${index}`}
                            aria-current={
                              activeSubmenuIndex?.menuIndex === index &&
                              activeSubmenuIndex?.subIndex === i
                                ? "page"
                                : undefined
                            }
                            className={`submenu-item gx-p14-b ${
                              activeSubmenuIndex?.menuIndex === index &&
                              activeSubmenuIndex?.subIndex === i
                                ? "active"
                                : ""
                            }`}
                            onClick={() => {
                              if (sub.menuUrl) {
                                setActiveMenuIndex(index);
                                setActiveSubmenuIndex({
                                  menuIndex: index,
                                  subIndex: i,
                                });
                                navigate(sub.menuUrl);
                              }
                            }}
                            onKeyDown={(e) =>
                              handleKeyDown(e, () => {
                                if (sub.menuUrl) {
                                  setActiveMenuIndex(index);
                                  setActiveSubmenuIndex({
                                    menuIndex: index,
                                    subIndex: i,
                                  });
                                  navigate(sub.menuUrl);
                                }
                              })
                            }
                          >
                            {sub.menuName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* ---------------- BOTTOM MENU ---------------- */}
              <div className="bottom-section">
                {menuBottom?.map((menu, idx) => {
                  const realIndex = idx + 9;
                  return (
                    <div key={realIndex} className="menu-section">
                      <div
                        className={`menu-item ${
                          activeBottomIndex === idx ? "active" : ""
                        }`}
                        aria-label={
                          isToggled
                            ? "Expand navigation menu"
                            : "Collapse navigation menu"
                        }
                        aria-expanded={!isToggled}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (menu.path) {
                            setActiveBottomIndex(idx);
                            setActiveSubmenuIndex(null);
                            navigate(menu.path);
                          }
                        }}
                      >
                        <div className="icon-container">
                          <img
                            src={`${ICON_BASE_PATH}${menu.icon}`}
                            width={22}
                            className="menu-icon"
                            alt={menu.title}
                            aria-label={menu.title}
                          />
                        </div>

                        {!isToggled && <span className="gx-p14-b">{menu.title}</span>}
                      </div>
                    </div>
                  );
                })}

                {/* ..........Footer........... */}
                {/* <Footer /> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* -------------------- CFO Sidebar -------------------- */}
      {isCFO && (
        <div
          className={`sidebar-sub-container ${isToggled ? "collapsed" : "expanded"}`}
        >
          <div className="gx-sidebar">
            <div className="gx-logoWrapper flex items-center justify-between">
              <img
                className="growX-logo"
                src={`${IMAGE_BASE_PATH}logosider.png`}
                alt="Logo"
                width={165}
                height={45}
              />

              <div
                className="cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={
                  isToggled
                    ? "Expand navigation menu"
                    : "Collapse navigation menu"
                }
                aria-expanded={!isToggled}
                onClick={() => setIsToggled(!isToggled)}
                onKeyDown={(e) =>
                  handleKeyDown(e, () => setIsToggled(!isToggled))
                }
              >
                <div className="gx-side-close-imge">
                  <img width={40} src={`${IMAGE_BASE_PATH}sidebarlogo.png`} />
                </div>
                <div className="gx-toggle-icon">
                  <img
                    src={`${IMAGE_BASE_PATH}arrowsider.svg`}
                    alt={isToggled ? "Collapse" : "Expand"}
                    width={18}
                    height={16}
                    style={{
                      transform: isToggled ? "rotate(90deg)" : "rotate(280deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="gx-sidebar-menu">
              {/* ---------------- TOP MENU ---------------- */}
              <div className="top-section">
                {menuConfig.map((item, index) => (
                  <div key={index} className="menu-section">
                    <div
                      className={`menu-item gx-p12-eb ${
                        activeCfoIndex === index ? "active" : ""
                      }`}
                      role="button"
                      tabIndex={0}
                      aria-label={item.label}
                      onClick={() => {
                        if (item.path) {
                          navigate(item.path);
                        }
                      }}
                    >
                      <div className="icon-container">
                        <img
                          src={`${ICON_BASE_PATH}${item.icon}`}
                          width={22}
                          className="menu-icon"
                          alt={item.label}
                          aria-label={item.label}
                        />
                      </div>

                      {!isToggled && <span>{item.label}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* ---------------- BOTTOM MENU ---------------- */}
              <div className="bottom-section">
                <div
                  className="gx-leave-co-Pilot"
                  role="button"
                  tabIndex={0}
                  aria-label="Leave CFO Co-Pilot"
                  onClick={() => navigate("/dashboard")}
                  onKeyDown={(e) =>
                    handleKeyDown(e, () => navigate("/dashboard"))
                  }
                >
                  <img
                    src={`${ICON_BASE_PATH}copilotlogout.svg`}
                    alt="Leave Co-Pilot"
                    aria-label="Leave Co-Pilot"
                  />

                  {!isToggled && (
                    <span className="gx-p16-sb">Leave Co-Pilot</span>
                  )}
                </div>

                {!isToggled && (
                  <div className="gx-sidebar-last">
                    <p className="gx-p10-m">Last Sync: 25 secs ago</p>
                    <p className="gx-p10-m">Data Sources: ERP, Bank, Billing</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
